import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  CardHeader,
  CardFooter,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Spinner,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { get, put } from "utils/api";
import CustomPagination from "./CustomPagination";
import { useSearch } from "views/Contexts/SearchContext";
import { toast } from "react-toastify";

const PaymentDistributions = () => {
  const { token } = useSearch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal states
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const fetchDistributions = async () => {
    setLoading(true);
    try {
      // Fetch all records
      const response = await get("/api/admin/distributions", {}, headers);
      if (response && response.data) {
        // Exclude Druyp fee rows — those are handled internally
        const filtered = (response.data || []).filter(
          (r) => r.receiver_type?.toLowerCase() !== "druyp"
        );
        setTotalData(filtered.length);
        // Manual pagination
        const startIndex = (pageNo - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        setData(filtered.slice(startIndex, endIndex));
      } else {
        setData([]);
        setTotalData(0);
      }
    } catch (error) {
      console.error("Error fetching distributions:", error);
      toast.error("Failed to load payment distributions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributions();
    // eslint-disable-next-line
  }, [pageNo]);

  const toggleConfirmModal = () => {
    setIsConfirmModalOpen(!isConfirmModalOpen);
  };

  const handlePayClick = (record) => {
    setSelectedRecord(record);
    setIsConfirmModalOpen(true);
  };

  const handleExecutePayout = async () => {
    if (!selectedRecord) return;
    const { id } = selectedRecord;

    setActionLoading((prev) => ({ ...prev, [id]: true }));
    setIsConfirmModalOpen(false); // Close confirmation modal immediately

    try {
      const response = await put(`/api/admin/distributions/${id}/pay`, {}, headers);
      if (response && response.message) {
        toast.success(response.message || "Payout executed successfully!");
      } else {
        toast.success("Payout executed successfully!");
      }
      fetchDistributions(); // Refresh list to reflect updated paid status
    } catch (error) {
      console.error("Payout execution error:", error);
      // The API handler in utils/api.js will automatically fire a toast.error, 
      // but in case it doesn't, we show a general error toast here.
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }));
      setSelectedRecord(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return (
          <Badge color="warning" className="badge-dot mr-4">
            <i className="bg-warning" /> Pending
          </Badge>
        );
      case "paid":
        return (
          <Badge color="success" className="badge-dot mr-4">
            <i className="bg-success" /> Paid
          </Badge>
        );
      default:
        return <Badge color="secondary">{status || "Unknown"}</Badge>;
    }
  };

  const formatReceiverType = (type) => {
    switch (type?.toLowerCase()) {
      case "homeowner":
        return <Badge color="info">Home Owner</Badge>;
      case "cleaning_company":
        return <Badge color="primary">Cleaning Company</Badge>;
      case "druyp":
        return <Badge color="default" style={{ backgroundColor: "#5e72e4", color: "#fff" }}>Druyp</Badge>;
      default:
        return <Badge color="secondary">{type || "N/A"}</Badge>;
    }
  };

  const formatDistributionType = (type) => {
    if (!type) return "N/A";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <>
      <Header title="Payment Distributions" />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Disbursements & Splits</h3>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">SR. No</th>
                    <th scope="col">Receiver Type</th>
                    <th scope="col">Distribution Type</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5">
                        <Spinner color="primary" />
                      </td>
                    </tr>
                  ) : data && data.length > 0 ? (
                    data.map((record, index) => (
                      <tr key={record.id}>
                        <td className="font-weight-bold">{index + 1}</td>
                        <td>{formatReceiverType(record.receiver_type)}</td>
                        <td className="text-capitalize">
                          {formatDistributionType(record.distribution_type)}
                        </td>
                        <td className="font-weight-bold text-dark">
                          ${Number(record.amount).toFixed(2)}
                        </td>
                        <td>{getStatusBadge(record.status)}</td>
                        <td className="text-right">
                          {record.status?.toLowerCase() === "pending" ? (
                            <Button
                              style={{ background: "#0474ba", color: "#ffff", minWidth: "120px" }}
                              size="sm"
                              disabled={actionLoading[record.id]}
                              onClick={() => handlePayClick(record)}
                            >
                              {actionLoading[record.id] ? (
                                <>
                                  <Spinner size="sm" className="mr-2" /> Processing
                                </>
                              ) : (
                                "Transfer Funds"
                              )}
                            </Button>
                          ) : (
                            <Button
                              color="success"
                              outline
                              size="sm"
                              disabled
                              style={{ minWidth: "120px" }}
                            >
                              <i className="fa-solid fa-check mr-1" /> Completed
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted">
                        No payment distribution records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <CardFooter className="py-4">
                <nav aria-label="...">
                  <CustomPagination
                    totalItems={totalData}
                    itemsPerPage={itemsPerPage}
                    currentPage={pageNo}
                    onPageChange={(page) => setPageNo(page)}
                  />
                </nav>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Confirmation Payout Modal */}
      <Modal isOpen={isConfirmModalOpen} toggle={toggleConfirmModal} centered size="md">
        <ModalHeader toggle={toggleConfirmModal} className="bg-gradient-primary text-white border-0">
          <h4 className="modal-title text-white">Confirm Fund Transfer</h4>
        </ModalHeader>
        <ModalBody className="py-4 text-center">
          <div className="icon icon-shape bg-warning text-white rounded-circle shadow mb-4">
            <i className="fa-solid fa-triangle-exclamation fa-2x text-white" />
          </div>
          {selectedRecord && (
            <div>
              <p className="lead font-weight-normal text-muted mb-4">
                Are you sure you want to process this payout? The funds will be transferred directly to the receiver's account. This action cannot be undone.
              </p>
              <div className="card bg-secondary shadow-none border p-3 text-left">
                <Row className="mb-2">
                  <span className="col-5 text-muted small uppercase">Distribution ID:</span>
                  <span className="col-7 font-weight-bold">#{selectedRecord.id}</span>
                </Row>
                <Row className="mb-2">
                  <span className="col-5 text-muted small uppercase">Receiver Type:</span>
                  <span className="col-7 text-capitalize font-weight-bold">
                    {selectedRecord.receiver_type?.replace("_", " ")}
                  </span>
                </Row>
                <Row className="mb-2">
                  <span className="col-5 text-muted small uppercase">Split Type:</span>
                  <span className="col-7 font-weight-bold">
                    {formatDistributionType(selectedRecord.distribution_type)}
                  </span>
                </Row>
                <Row>
                  <span className="col-5 text-muted small uppercase">Payout Amount:</span>
                  <span className="col-7 text-success font-weight-bold h4 mb-0">
                    ${Number(selectedRecord.amount).toFixed(2)}
                  </span>
                </Row>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="border-0 bg-secondary py-3">
          <Button color="secondary" outline onClick={toggleConfirmModal} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleExecutePayout} disabled={loading}>
            Execute Transfer
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PaymentDistributions;
